<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  type PropType,
  ref,
  type Ref,
  watch,
} from "vue";
import {
  type SpectroInfo,
  spectroToCenter,
  spectroTimeToX,
  spectroXToTime,
  useGeoJS,
} from "./geoJS/geoJSUtils";
import {
  patchAnnotation,
  patchSequenceAnnotation,
  putAnnotation,
  putSequenceAnnotation,
  type SpectrogramAnnotation,
  type SpectrogramSequenceAnnotation,
} from "../api/api";
import LayerManager from "./geoJS/LayerManager.vue";
import PulseMetadataTooltip from "./PulseMetadataTooltip.vue";
import type { PulseMetadataTooltipData } from "./geoJS/layers/pulseMetadataLayer";
import type { GeoEvent } from "geojs";
import geo from "geojs";
import useState from "@use/useState";
import { getImageDimensions } from "@use/useUtils";

export default defineComponent({
  name: "SpectroViewer",
  components: { LayerManager, PulseMetadataTooltip },
  props: {
    images: { type: Array as PropType<HTMLImageElement[]>, default: () => [] },
    maskImages: {
      type: Array as PropType<HTMLImageElement[]>,
      default: () => [],
    },
    maskLoaded: {
      type: Boolean,
      default: false,
    },
    waveplotImages: {
      type: Array as PropType<HTMLImageElement[]>,
      default: () => [],
    },
    spectroInfo: { type: Object as PropType<SpectroInfo>, required: true },
    recordingId: { type: String as PropType<string | null>, required: true },
    compressed: { type: Boolean, required: true },
    restoreCenterTimeMs: {
      type: Number as PropType<number | null>,
      default: null,
    },
  },
  emits: [
    "update:annotation",
    "create:annotation",
    "geoViewerRef",
    "hoverData",
    "restoreCenterComplete",
  ],
  setup(props, { emit }) {
    const {
      annotations,
      sequenceAnnotations,
      selectedId,
      selectedType,
      creationType,
      backgroundColor,
      scaledVals,
      configuration,
      scaledWidth,
      scaledHeight,
      contoursEnabled,
      imageOpacity,
      viewMaskOverlay,
      viewWaveplot,
      maskOverlayOpacity,
    } = useState();

    const containerRef: Ref<HTMLElement | undefined> = ref();
    const pulseMetadataTooltipData = ref<PulseMetadataTooltipData | null>(null);
    const geoJS = useGeoJS();
    const initialized = ref(false);
    const cursor = ref("");
    const imageCursorRef: Ref<HTMLElement | undefined> = ref();
    const restoredCenterTimeMs = ref<number | null>(null);

    const setCursor = (newCursor: string) => {
      cursor.value = newCursor;
    };

    const baseDimensions = computed(() =>
      getImageDimensions(props.images, props.spectroInfo),
    );
    const waveplotDisplayHeight = computed(() =>
      Math.floor(baseDimensions.value.height / 5),
    );
    const showWaveplot = computed(
      () =>
        viewWaveplot.value &&
        props.waveplotImages.length &&
        waveplotDisplayHeight.value > 0,
    );
    const totalDisplayHeight = computed(
      () =>
        scaledHeight.value +
        (showWaveplot.value ? waveplotDisplayHeight.value : 0),
    );

    function updateScaledDimensions() {
      const { width, height } = getImageDimensions(
        props.images,
        props.spectroInfo,
      );
      scaledWidth.value = width * scaledVals.value.x;
      scaledHeight.value = height * scaledVals.value.y;
      if (scaledWidth.value < width) {
        scaledWidth.value = width;
      }
      if (scaledHeight.value < height) {
        scaledHeight.value = height;
      }
    }

    const cursorHandler = {
      handleMouseLeave() {
        if (imageCursorRef.value) {
          imageCursorRef.value.style.display = "none";
        }
      },
      handleMouseEnter() {
        if (imageCursorRef.value) {
          imageCursorRef.value.style.display = "block";
        }
      },
      handleMouseMove(evt: MouseEvent) {
        const offsetX = evt.clientX + 10;
        const offsetY = evt.clientY - 25;
        window.requestAnimationFrame(() => {
          if (imageCursorRef.value) {
            imageCursorRef.value.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
          }
        });
      },
    };

    const mouseMoveEvent = (e: GeoEvent) => {
      if (!props.spectroInfo) return;
      const { x, y } = e.geo;
      const height = Math.max(scaledHeight.value, props.spectroInfo.height);

      const freq =
        height - y >= 0
          ? ((height - y) *
              (props.spectroInfo.high_freq - props.spectroInfo.low_freq)) /
              height /
              1000 +
            props.spectroInfo.low_freq / 1000
          : -1;

      let time = -1;
      if (x >= 0 && height - y >= 0) {
        time = spectroXToTime(x, props.spectroInfo, scaledWidth.value);
      }
      emit("hoverData", { time, freq });
    };

    const effectiveImageOpacity = computed(() =>
      contoursEnabled.value ? imageOpacity.value : 1,
    );

    function resetViewerBounds(resetCam = false) {
      const viewer = geoJS.getGeoViewer().value;
      if (!viewer) return;
      const totalHeight = totalDisplayHeight.value;
      const prevCenter = viewer.center?.();
      const prevZoom = viewer.zoom?.();

      // Always reset maxBounds/clamping to current content size, but avoid the default
      // "fit everything" camera reset so the spectrogram loads flush left.
      geoJS.resetMapDimensions(scaledWidth.value, totalHeight, 0.3, false);

      if (!resetCam) {
        // Preserve current view when just resizing content extents.
        if (prevZoom != null) viewer.zoom(prevZoom);
        if (prevCenter != null) viewer.center(prevCenter);
        return;
      }

      const viewport = viewer.camera?.()?.viewport;
      const viewportW = viewport?.width ?? 0;
      const viewportH = viewport?.height ?? 0;
      const aspect = viewportW > 0 && viewportH > 0 ? viewportW / viewportH : 1;

      // Keep the initial view left-aligned with no leading gutter while fitting the
      // full vertical height. If the content is narrower than the viewport, any extra
      // space should appear on the right instead of centering the image.
      const desiredWidth = Math.max(200, totalHeight * aspect);
      const left = 0;
      const right = left + desiredWidth;
      const viewBounds = { left, top: 0, right, bottom: totalHeight };

      const zoomAndCenter = viewer.zoomAndCenterFromBounds(viewBounds, 0);
      viewer.zoom(zoomAndCenter.zoom);
      viewer.center(zoomAndCenter.center);
    }

    function restoreCenterIfNeeded() {
      const requestedCenterTime = props.restoreCenterTimeMs;
      if (requestedCenterTime === null) {
        restoredCenterTimeMs.value = null;
        return;
      }
      if (requestedCenterTime === restoredCenterTimeMs.value) {
        return;
      }

      const viewer = geoJS.getGeoViewer().value;
      const currentCenter = viewer?.center?.();
      if (!viewer || !currentCenter) {
        return;
      }

      const nextX = spectroTimeToX(
        requestedCenterTime,
        props.spectroInfo,
        scaledWidth.value,
      );
      if (nextX < 0) {
        return;
      }

      viewer.center({ x: nextX, y: currentCenter.y });
      restoredCenterTimeMs.value = requestedCenterTime;
      emit("restoreCenterComplete", requestedCenterTime);
    }

    function drawWaveplotIfEnabled() {
      if (showWaveplot.value) {
        geoJS.drawWaveplotImages(
          props.waveplotImages,
          scaledWidth.value,
          waveplotDisplayHeight.value,
          scaledHeight.value,
          1,
        );
      } else {
        geoJS.clearWaveplotQuadFeatures(true);
      }
    }

    function initializeViewerAndImages() {
      updateScaledDimensions();
      const totalHeight = totalDisplayHeight.value;
      if (containerRef.value && !geoJS.getGeoViewer().value) {
        geoJS.initializeViewer(
          containerRef.value,
          scaledWidth.value,
          totalHeight,
          false,
          props.images.length,
        );
        geoJS.getGeoViewer().value.geoOn(geo.event.mousemove, mouseMoveEvent);
      }
      if (props.images.length) {
        geoJS.drawImages(
          props.images,
          scaledWidth.value,
          scaledHeight.value,
          true,
          effectiveImageOpacity.value,
        );
      }
      if (viewMaskOverlay.value && props.maskImages.length) {
        geoJS.drawMaskImages(
          props.maskImages,
          scaledWidth.value,
          scaledHeight.value,
          maskOverlayOpacity.value,
        );
      }
      resetViewerBounds(true);
      initialized.value = true;
      emit("geoViewerRef", geoJS.getGeoViewer());

      if (props.compressed) {
        scaledVals.value = {
          x: configuration.value.spectrogram_x_stretch,
          y: 1,
        };
        updateScaledDimensions();
        resetViewerBounds(true);
        if (props.images.length) {
          geoJS.drawImages(
            props.images,
            scaledWidth.value,
            scaledHeight.value,
            false,
            effectiveImageOpacity.value,
          );
        }
        if (viewMaskOverlay.value && props.maskImages.length) {
          geoJS.drawMaskImages(
            props.maskImages,
            scaledWidth.value,
            scaledHeight.value,
            maskOverlayOpacity.value,
          );
        }
        drawWaveplotIfEnabled();
      }
      restoreCenterIfNeeded();
    }

    onMounted(() => {
      initialized.value = false;
      scaledHeight.value = 0;
      scaledWidth.value = 0;
      scaledVals.value = { x: 1, y: 1 };
    });

    watch(
      [containerRef, () => props.spectroInfo, () => props.images],
      initializeViewerAndImages,
    );

    watch(
      () => props.spectroInfo,
      () => {
        updateScaledDimensions();
        resetViewerBounds(true);
        if (props.images.length) {
          geoJS.drawImages(
            props.images,
            scaledWidth.value,
            scaledHeight.value,
            true,
            effectiveImageOpacity.value,
          );
        }
        if (viewMaskOverlay.value && props.maskImages.length) {
          geoJS.drawMaskImages(
            props.maskImages,
            scaledWidth.value,
            scaledHeight.value,
            maskOverlayOpacity.value,
          );
        }
        drawWaveplotIfEnabled();
        restoreCenterIfNeeded();
      },
    );

    watch(
      () => props.restoreCenterTimeMs,
      () => {
        if (props.restoreCenterTimeMs === null) {
          restoredCenterTimeMs.value = null;
          return;
        }
        restoreCenterIfNeeded();
      },
    );

    const updateAnnotation = async (
      annotation: SpectrogramAnnotation | SpectrogramSequenceAnnotation,
    ) => {
      if (props.recordingId !== null && selectedId.value !== null) {
        if (selectedType.value === "pulse") {
          await patchAnnotation(
            props.recordingId,
            selectedId.value,
            annotation,
          );
        } else if (selectedType.value === "sequence") {
          await patchSequenceAnnotation(
            props.recordingId,
            selectedId.value,
            annotation,
          );
        }
        emit("update:annotation", annotation);
      }
    };

    const createAnnotation = async (
      annotation: SpectrogramAnnotation | SpectrogramSequenceAnnotation,
    ) => {
      if (props.recordingId !== null) {
        if (creationType.value === "pulse") {
          const response = await putAnnotation(props.recordingId, annotation);
          emit("create:annotation", response.data.id);
        } else if (creationType.value === "sequence") {
          const response = await putSequenceAnnotation(
            props.recordingId,
            annotation,
          );
          emit("create:annotation", response.data.id);
        }
      }
    };

    let skipNextSelected = false;
    watch(selectedId, () => {
      if (skipNextSelected) {
        skipNextSelected = false;
        return;
      }
      const found =
        selectedType.value === "pulse"
          ? annotations.value.find((item) => item.id === selectedId.value)
          : sequenceAnnotations.value.find(
              (item) => item.id === selectedId.value,
            );
      if (found && props.spectroInfo) {
        const [x, y] = spectroToCenter(
          found,
          props.spectroInfo,
          selectedType.value,
          scaledWidth.value,
          scaledHeight.value,
        );
        const viewer = geoJS.getGeoViewer().value;
        if (viewer && x >= 0 && y >= 0) {
          viewer.center({ x, y });
        }
      }
    });

    const wheelEvent = (event: WheelEvent) => {
      const incrementX = 0.1,
        incrementY = 0.1;

      if (event.ctrlKey) {
        scaledVals.value.x += event.deltaY > 0 ? -incrementX : incrementX;
        if (scaledVals.value.x < 1) scaledVals.value.x = 1;
        updateScaledDimensions();
        resetViewerBounds(false);
        if (props.images.length) {
          geoJS.drawImages(
            props.images,
            scaledWidth.value,
            scaledHeight.value,
            false,
            effectiveImageOpacity.value,
          );
        }
        if (viewMaskOverlay.value && props.maskImages.length) {
          geoJS.drawMaskImages(
            props.maskImages,
            scaledWidth.value,
            scaledHeight.value,
            maskOverlayOpacity.value,
          );
        }
        drawWaveplotIfEnabled();
      } else if (event.shiftKey) {
        scaledVals.value.y += event.deltaY > 0 ? -incrementY : incrementY;
        if (scaledVals.value.y < 1) scaledVals.value.y = 1;
        updateScaledDimensions();
        resetViewerBounds(false);
        if (props.images.length) {
          geoJS.drawImages(
            props.images,
            scaledWidth.value,
            scaledHeight.value,
            false,
            effectiveImageOpacity.value,
          );
        }
        if (viewMaskOverlay.value && props.maskImages.length) {
          geoJS.drawMaskImages(
            props.maskImages,
            scaledWidth.value,
            scaledHeight.value,
            maskOverlayOpacity.value,
          );
        }
        drawWaveplotIfEnabled();
      }
    };

    onUnmounted(() => geoJS.destroyGeoViewer());

    watch([contoursEnabled, imageOpacity], () => {
      if (props.images.length) {
        geoJS.drawImages(
          props.images,
          scaledWidth.value,
          scaledHeight.value,
          false,
          effectiveImageOpacity.value,
        );
      }
    });

    watch(
      [
        viewMaskOverlay,
        maskOverlayOpacity,
        () => props.maskImages,
        () => props.maskLoaded,
      ],
      () => {
        if (viewMaskOverlay.value && props.maskImages.length) {
          geoJS.drawMaskImages(
            props.maskImages,
            scaledWidth.value,
            scaledHeight.value,
            maskOverlayOpacity.value,
          );
        } else {
          geoJS.clearMaskQuadFeatures(true);
        }
      },
    );

    watch([showWaveplot], () => {
      resetViewerBounds(false);
      drawWaveplotIfEnabled();
    });

    function onPulseMetadataTooltip(data: PulseMetadataTooltipData | null) {
      pulseMetadataTooltipData.value = data;
    }

    return {
      containerRef,
      pulseMetadataTooltipData,
      onPulseMetadataTooltip,
      geoViewerRef: geoJS.getGeoViewer(),
      initialized,
      cursor,
      setCursor,
      updateAnnotation,
      createAnnotation,
      cursorHandler,
      imageCursorRef,
      wheelEvent,
      scaledWidth,
      scaledHeight,
      backgroundColor,
    };
  },
});
</script>

<template>
  <div
    class="video-annotator"
    :style="{ backgroundColor: backgroundColor }"
    @wheel="wheelEvent($event)"
  >
    <div
      id="spectro"
      ref="containerRef"
      class="playback-container playback-container--with-tooltip"
      :style="{ cursor: cursor }"
      @mousemove="cursorHandler.handleMouseMove"
      @mouseleave="cursorHandler.handleMouseLeave"
      @mouseover="cursorHandler.handleMouseEnter"
    >
      <PulseMetadataTooltip :data="pulseMetadataTooltipData" />
    </div>
    <layer-manager
      v-if="initialized"
      :geo-viewer-ref="geoViewerRef"
      :spectro-info="spectroInfo"
      :scaled-width="scaledWidth"
      :scaled-height="scaledHeight"
      :recording-id="recordingId"
      @update:annotation="updateAnnotation($event)"
      @create:annotation="createAnnotation($event)"
      @set-cursor="setCursor($event)"
      @pulse-metadata-tooltip="onPulseMetadataTooltip($event)"
    />
    <div ref="imageCursorRef" class="imageCursor">
      <v-icon color="white">
        {{ cursor }}
      </v-icon>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.video-annotator {
  position: relative;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 0;

  display: flex;
  flex-direction: column;

  .geojs-map {
    margin: 2px;

    &.geojs-map:focus {
      outline: none;
    }
  }

  .playback-container {
    flex: 1;
    position: relative;
  }

  .loadingSpinnerContainer {
    z-index: 20;
    margin: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    -ms-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
  }

  .geojs-map.annotation-input {
    cursor: inherit;
  }
}

.black-background {
  background-color: black;
}

.white-background {
  background-color: white;
}

.imageCursor {
  z-index: 9999; //So it will be above the annotator layers
  position: fixed;
  backface-visibility: hidden;
  top: 0;
  left: 0;
  pointer-events: none;
}
</style>
